import { useEffect } from "react";
import { scoreListing, colorForListingScore } from "../lib/listingScoring.js";
import { formatEUR, formatArea, prettyConcelho, loc, locFire } from "../lib/localization.js";
import { CashflowSection } from "./CashflowSection.jsx";
import { TcoBreakdown } from "./TcoBreakdown.jsx";
import { NotesField } from "./NotesField.jsx";
import { useModalHistory } from "../lib/useModalHistory.js";

export function ListingModal({ listing, towns, concelhoBySlug, weights, enforceVeto, retreatMode, favorites, onWorkflowChange, onClose , canEdit = true }) {
  const close = useModalHistory(onClose);
  useEffect(() => {
    const h = e => e.key === "Escape" && close();
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [close]);

  const { score, veto, breakdown, townName } = scoreListing(listing, towns, concelhoBySlug, weights, enforceVeto, retreatMode);
  const concelho = concelhoBySlug[listing.concelho];
  const wf = favorites?.[listing.url] || { is_favorite: false, status: "" };
  const toggleFav = () => onWorkflowChange(listing.url, { ...wf, is_favorite: !wf.is_favorite });
  const setStatus = (s) => onWorkflowChange(listing.url, { ...wf, status: s });

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && close()}>
      <div className="modal-shell">
        <header className="modal-header">
          <button className="back-btn modal-back" onClick={close}><span className="back-arrow">←</span><span className="back-label">Terug</span></button>
          <div className="header-title">
            <h2>{listing.title || "Quinta / Herdade"}</h2>
            <div className="meta-line">{listing.region} · {prettyConcelho(listing.concelho)} · <b>{formatEUR(listing.price_eur)}</b></div>
          </div>
          <button className="close-btn modal-close" onClick={close}>×</button>
        </header>
        <div className="modal-body">
          {listing.photo_url && (
            <div className="listing-hero">
              <img src={listing.photo_url} alt="" onError={e => (e.target.style.display = "none")} />
              <div className="price-tag">{formatEUR(listing.price_eur)}</div>
            </div>
          )}

          {concelho && (
            <div className="safety-bar">
              {concelho.fire_risk_class && <span className={`safety-pill fire-${concelho.fire_risk_class}`}>🔥 {locFire(concelho.fire_risk_class)}</span>}
              {concelho.water_status && <span className={`safety-pill water-${concelho.water_status}`}>💧 Water {loc("water_status", concelho.water_status)}</span>}
              {retreatMode && concelho.al_open !== null && concelho.al_open !== undefined && <span className="safety-pill">📝 AL {concelho.al_open ? "Open" : "Gesloten"}</span>}
            </div>
          )}

          <div className={`listing-match-hero ${veto.length ? "is-vetoed" : ""}`}>
            <div className="lmh-left">
              <span className="lmh-label">Match-score voor jullie</span>
              {veto.length
                ? <span className="lmh-veto-text"><b>Voldoet niet aan harde criteria:</b><br/>{veto.join(" · ")}</span>
                : <span className="lmh-sub">Berekend tegen jullie sliders + concelho-risico's{townName ? ` (basis: ${townName})` : ""}{retreatMode ? " — incl. retreat-business factors" : ""}</span>}
            </div>
            <div className="lmh-right">
              <span className="lmh-score" style={{ color: colorForListingScore(score, veto) }}>
                {veto.length ? "✗" : score}<small>{veto.length ? "" : "/100"}</small>
              </span>
            </div>
          </div>

          {breakdown.length > 0 && (
            <details className="lmh-breakdown" open>
              <summary>Waarom deze score? — {breakdown.length} factor{breakdown.length === 1 ? "" : "en"}</summary>
              <table className="lmh-table">
                <tbody>
                  {breakdown.map((b, i) => (
                    <tr key={i} className={`lmh-row lmh-${b.kind}`}>
                      <td className="lmh-r-label">{b.label}</td>
                      <td className="lmh-r-delta">{b.delta > 0 ? "+" : ""}{b.delta}</td>
                    </tr>
                  ))}
                  <tr className="lmh-row lmh-total">
                    <td className="lmh-r-label">Totaal (gecapt 0–100)</td>
                    <td className="lmh-r-delta">{score}</td>
                  </tr>
                </tbody>
              </table>
            </details>
          )}

          <div className="modal-cols">
            <section>
              <h4>Kerngegevens</h4>
              <div className="house-key">
                <div className="kv"><span>Prijs</span><b>{formatEUR(listing.price_eur)}</b></div>
                <div className="kv"><span>Grond</span><b>{formatArea(listing.area_total_m2)}</b></div>
                {listing.area_built_m2 && <div className="kv"><span>Bebouwd</span><b>{listing.area_built_m2} m²</b></div>}
                {listing.bedrooms && <div className="kv"><span>Slaapkamers</span><b>T{listing.bedrooms}</b></div>}
                {listing.price_eur && listing.area_total_m2 && <div className="kv"><span>€/m²</span><b>€ {Math.round(listing.price_eur/listing.area_total_m2)}</b></div>}
              </div>
              <h4 style={{ marginTop: 20 }}>Workflow</h4>
              <div className="workflow-buttons">
                <button className={`wf-btn fav ${wf.is_favorite ? "on" : ""}`} onClick={toggleFav}>{wf.is_favorite ? "★ Favoriet" : "☆ Favoriet"}</button>
                <select className="wf-status" value={wf.status} onChange={e => setStatus(e.target.value)}>
                  <option value="">— Status —</option>
                  <option value="interesting">Interessant</option>
                  <option value="visited">Bezocht</option>
                  <option value="offered">Bod uit</option>
                  <option value="rejected">Afgewezen</option>
                </select>
                <a className="wf-link" href={listing.url} target="_blank" rel="noopener">Open listing ↗</a>
              </div>
            </section>
            <section>
              {listing.description && (<>
                <h4>Beschrijving</h4>
                <div className="description">{listing.description.slice(0, 600)}{listing.description.length > 600 ? "…" : ""}</div>
              </>)}
              <h4 style={{ marginTop: 20 }}>Bron + tijdlijn</h4>
              <div className="meta-block">
                <b>Eerst gezien:</b><span>{listing.first_seen ? new Date(listing.first_seen).toLocaleDateString("nl-NL") : "—"}</span>
                <b>Laatst gezien:</b><span>{listing.last_seen ? new Date(listing.last_seen).toLocaleDateString("nl-NL") : "—"}</span>
                <b>Bron:</b><span>{listing.source}</span>
              </div>
            </section>
          </div>

          {/* Patch #31: Total Cost of Ownership — altijd zichtbaar */}
          {listing.price_eur && (
            <TcoBreakdown listing={listing} concelho={concelho} mode="listing" />
          )}

          {/* Cashflow ALLEEN bij retreat-mode aan */}
          {retreatMode && listing.price_eur && (
            <CashflowSection
              purchasePrice={listing.price_eur}
              bedrooms={listing.bedrooms || 3}
              concelho={concelho}
              subtitle={`Retreat-business modus — gebaseerd op werkelijke prijs (${formatEUR(listing.price_eur)})${listing.bedrooms ? ` en T${listing.bedrooms}` : ""}.`}
            />
          )}

          <NotesField id={"listing:" + listing.url} placeholder="Vragen voor de makelaar, observaties, status..." readOnly={!canEdit} />
        </div>
      </div>
    </div>
  );
}
